import _ from 'lodash';
import { useEffect } from 'react';
import type { ApolloError } from '@apollo/client';
import useNavigate from '../router/useNavigate';
import { UIActions } from '../../ui/uiSlice';
import handleApolloError from './handleApolloError';
import type { Dispatch } from '../../app/redux/useAppSelector';
import { useAppDispatch } from '../../app/redux/useAppSelector';

interface ShowErrorAndRedirectOptions {
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
  message: string;
  title?: string;
}

const redirectOnError = ({
  dispatch,
  navigate,
  message,
  title,
}: ShowErrorAndRedirectOptions) => {
  dispatch(UIActions.pushToast({ level: 'error', message, title }));
  navigate('/', { replace: true });
};

type NOUN = 'topic' | 'post' | 'group' | 'user';

interface RedirectForErrorOptions {
  noun: NOUN;
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
}

export const redirectOnNotFoundError = ({
  noun,
  dispatch,
  navigate,
}: RedirectForErrorOptions) =>
  redirectOnError({
    dispatch,
    navigate,
    message: `${_.capitalize(noun)} not found`,
    title: 'Oops',
  });

export const redirectOnForbiddenError = ({
  noun,
  dispatch,
  navigate,
}: RedirectForErrorOptions) =>
  redirectOnError({
    dispatch,
    navigate,
    message: `You do not have permission to view this ${noun}`,
  });

interface UseErrorRedirectEffectOptions {
  noun: NOUN;
  error?: ApolloError;
}

const handleError = ({
  dispatch,
  error,
  noun,
  navigate,
}: {
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
  error: ApolloError;
} & UseErrorRedirectEffectOptions) => {
  handleApolloError(error, {
    NOT_FOUND: () => {
      redirectOnNotFoundError({ noun, dispatch, navigate });
    },
    FORBIDDEN: () => {
      redirectOnForbiddenError({ noun, dispatch, navigate });
    },
  });
};

const useErrorRedirectEffect = (options: UseErrorRedirectEffectOptions) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (options.error) {
      handleError({ dispatch, ...options, error: options.error, navigate });
    }
  });
};

export default useErrorRedirectEffect;
